import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { categories } from '@/api/endpoints';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { CategoryNode } from '@/types/api';

/** How many of the top item-count categories the dashboard breakdown shows. */
const MAX_BREAKDOWN_CATEGORIES = 5;

/** A flattened, dashboard-relevant slice of CategoryNode (no children/nesting). */
export interface RankedCategory {
  id: number;
  nameTr: string;
  nameEn: string;
  itemCount: number;
}

function flatten(nodes: CategoryNode[]): RankedCategory[] {
  return nodes.flatMap((n) => [
    { id: n.id, nameTr: n.nameTr, nameEn: n.nameEn, itemCount: n.itemCount },
    ...flatten(n.children),
  ]);
}

/**
 * Flattens the category tree (parents and children alike compete for the same
 * ranking — this is a "what do I own the most of" view, not a hierarchy) and
 * returns the top categories by item count, excluding empty ones. Ties keep
 * the API's original order (Array#sort is stable), same rationale as
 * ItemsPage's category chips.
 */
export function topCategoriesByItemCount(
  nodes: CategoryNode[],
  limit = MAX_BREAKDOWN_CATEGORIES,
): RankedCategory[] {
  return flatten(nodes)
    .filter((c) => c.itemCount > 0)
    .sort((a, b) => b.itemCount - a.itemCount)
    .slice(0, limit);
}

/**
 * Small horizontal bar list of the top categories by item count, shown on the
 * dashboard below the stat cards. A brand-new account (or one with no
 * categorized items yet) has nothing to rank, so this renders nothing rather
 * than an empty or all-zero-looking chart.
 */
export function CategoryBreakdown() {
  const { t, i18n } = useTranslation();
  const tree = useQuery({ queryKey: ['categories', 'tree'], queryFn: categories.tree });

  // `isSuccess` (rather than `!isLoading && !isError`) narrows `tree.data` to a
  // defined array for TypeScript, so there's no separate undefined-data branch
  // to reason about below.
  if (!tree.isSuccess) return null;

  const topCategories = topCategoriesByItemCount(tree.data);
  if (topCategories.length === 0) return null;

  const maxCount = topCategories[0].itemCount;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.categoryBreakdown')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {topCategories.map((c) => {
          const name = i18n.language?.startsWith('tr') ? c.nameTr : c.nameEn;
          // A floor keeps a category with a much smaller count than the
          // leader from rendering as an invisible sliver.
          const widthPct = Math.max(6, Math.round((c.itemCount / maxCount) * 100));
          return (
            <div key={c.id} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 truncate sm:w-32" title={name}>
                {name}
              </span>
              <div
                className="h-2 flex-1 overflow-hidden rounded-pill bg-surface-2"
                role="progressbar"
                aria-label={name}
                aria-valuenow={c.itemCount}
                aria-valuemin={0}
                aria-valuemax={maxCount}
              >
                <div className="h-full rounded-pill bg-brand-500" style={{ width: `${widthPct}%` }} />
              </div>
              <span className="w-6 shrink-0 text-right text-muted-foreground">{c.itemCount}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
